import React, { useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import firebase from '../utilities/firebase';
import algolia from '../utilities/algolia';
import { StackedDialog, useStackZIndex, useSelectMenuProps } from '../utilities/ModalStackContext';

export default function CarAssociationDialog({ open, onClose, customerId, customerData, onSuccess }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [searchQuery, setSearchQuery] = useState('');
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [associationType, setAssociationType] = useState('consignor');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setCars([]);
      setSelectedCar(null);
      setAssociationType('consignor');
      setLoading(false);
      setSaving(false);
    }
  }, [open]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setCars([]);
      return;
    }

    setLoading(true);
    try {
      const index = algolia.client.initIndex('cars');
      const { hits } = await index.search(query);
      
      // Sort results by stock number (largest to smallest)
      const sortedHits = hits.sort((a, b) => {
        const stockA = a.stock ? parseInt(a.stock.replace(/\D/g, ''), 10) : 0;
        const stockB = b.stock ? parseInt(b.stock.replace(/\D/g, ''), 10) : 0;
        return stockB - stockA;
      });

      setCars(sortedHits);
    } catch (error) {
      console.error('Error searching cars:', error);
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCarSelect = (car) => {
    setSelectedCar(car);
    setCars([car]);
    setSearchQuery(`${car.year} ${car.make} ${car.model} ${car.stock}`);
  };

  const handleAssociate = async () => {
    if (!selectedCar || !customerId) return;

    setSaving(true);
    try {
      const db = firebase.firestore();
      const carId = selectedCar.stock || selectedCar.objectID;

      if (associationType === 'consignor') {
        // Associate as consignor in cars collection
        await db.collection('cars').doc(carId).set({
          consignor: customerId
        }, { merge: true });
      } else {
        // Associate as buyer or co-buyer in deals collection
        const dealData = {
          [associationType]: customerId
        };

        await db.collection('deals').doc(carId).set(dealData, { merge: true });
      }

      // Call success callback to refresh data
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error associating customer with car:', error);
      // You might want to show an error message to the user here
    } finally {
      setSaving(false);
    }
  };

  const getCarLabel = (car) => {
    return `${car.stock || ""}${!!car.stock ? " " : ""}${car.year || ""}${!!car.year ? " " : ""}${car.make || ""}${!!car.make ? " " : ""}${car.model || ""}`;
  };

  return (
    <StackedDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        Associate Customer with Car
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Customer Info */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Customer
              </Typography>
              <Typography variant="body1">
                {customerData ? `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() : 'Unknown Customer'}
              </Typography>
              {customerData?.email && (
                <Typography variant="body2" color="text.secondary">
                  {customerData.email}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Association Type */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Association Type</InputLabel>
              <Select
                value={associationType}
                onChange={(e) => setAssociationType(e.target.value)}
                label="Association Type"
                MenuProps={selectMenuProps}
              >
                <MenuItem value="consignor">Consignor (Car Owner)</MenuItem>
                <MenuItem value="buyer">Buyer</MenuItem>
                <MenuItem value="cobuyer">Co-Buyer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Car Search */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search Cars"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              margin="normal"
              placeholder="Search by stock number, make, model..."
              autoComplete="off"
            />
          </Grid>

          {/* Loading Indicator */}
          {loading && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            </Grid>
          )}

          {/* Car Results */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {cars.map((car) => (
                <Grid item xs={12} sm={6} md={4} key={car.objectID}>
                  <Card 
                    onClick={() => handleCarSelect(car)}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedCar?.objectID === car.objectID ? '2px solid #1976d2' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={car.thumbnail || '/missing_image.jpeg'}
                      alt={getCarLabel(car)}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6" component="div" noWrap>
                        {car.stock || 'No Stock'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {getCarLabel(car)}
                      </Typography>
                      {car.price && (
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          ${car.price.toLocaleString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Selected Car Summary */}
          {selectedCar && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                <Typography variant="h6" gutterBottom>
                  Selected Car
                </Typography>
                <Typography variant="body1">
                  {getCarLabel(selectedCar)}
                </Typography>
                <Typography variant="body2">
                  Will be associated as: <strong>{associationType === 'consignor' ? 'Consignor (Car Owner)' : associationType === 'buyer' ? 'Buyer' : 'Co-Buyer'}</strong>
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleAssociate} 
          variant="contained" 
          disabled={!selectedCar || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Associating...' : 'Associate'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
